import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, FilePlus2 } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function formatCompetencia(yyyymm: string | null | undefined): string {
  if (!yyyymm) return "—";
  const [yyyy, mm] = yyyymm.split("-");
  return `${MESES[parseInt(mm, 10) - 1]}/${yyyy}`;
}

function formatBRLCents(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface InstrucaoAtiva {
  competencia: string;
  tomadorNome: string;
  valor: number;
  nfStatus: string | null;
}

interface NfRecusada {
  id?: string;
  numero?: string;
  mesReferencia: string;
  status: string;
  /** Cópia de uma NF já enviada: pede a nota correta, não o cancelamento. */
  duplicada?: boolean;
}

interface BonificacaoAtiva {
  competencia: string;
  nome: string;
  valor: number;
  nfStatus: string | null;
}

interface BannerData {
  instrucao: InstrucaoAtiva | null;
  nfRecusada: NfRecusada | null;
  bonificacao?: BonificacaoAtiva | null;
}

const CompetenciaBanner = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const userId = user?.id;
  const ehClt = user?.vinculo === "clt";
  const [data, setData] = useState<BannerData | null | undefined>(undefined);

  useEffect(() => {
    // Só PJ emite nota (inclusive quem é RH ou Financeiro); CLT não tem instrução de NF.
    if (!userId || ehClt) { setData(null); return; }
    api.get<BannerData>("/instrucoes/me")
      .then((d) => setData(d))
      .catch(() => setData(null));
  }, [userId, ehClt]);

  if (data === undefined || data === null) return null;

  const { instrucao, nfRecusada, bonificacao } = data;

  // A NF de bonificação é separada da de salário: avisa junto, em outra linha.
  const avisoBonificacao =
    bonificacao && !bonificacao.nfStatus ? (
      <div className="bg-primary/10 border-b border-primary/30 px-4 py-2.5 text-sm">
        <strong>{bonificacao.nome}</strong> de <strong>{formatCompetencia(bonificacao.competencia)}</strong> liberada •
        Emitir NF de <strong>R$ {formatBRLCents(bonificacao.valor)}</strong>.{" "}
        <Link to="/colaborador/nova" className="font-medium underline underline-offset-4">
          Enviar na aba {bonificacao.nome}
        </Link>
      </div>
    ) : null;

  if (nfRecusada && nfRecusada.status === "Recusada" && nfRecusada.duplicada && nfRecusada.id) {
    return (
      <div className="bg-destructive/10 border-b border-destructive/30 px-4 py-2.5 text-sm text-destructive">
        <strong>Atenção:</strong> sua nota da competência{" "}
        <strong>{formatCompetencia(nfRecusada.mesReferencia)}</strong> foi recusada por ser cópia de uma nota já
        enviada. Não cancele: ela é a sua nota válida.{" "}
        <Link to={`/colaborador/nova?substituir=${nfRecusada.id}`} className="font-medium underline underline-offset-4">
          Envie a nota correta
        </Link>{" "}
        para substituí-la.
      </div>
    );
  }

  if (nfRecusada && nfRecusada.status === "Recusada") {
    return (
      <div className="bg-destructive/10 border-b border-destructive/30 px-4 py-2.5 text-sm text-destructive">
        <strong>Atenção:</strong> sua nota da competência{" "}
        <strong>{formatCompetencia(nfRecusada.mesReferencia)}</strong> foi recusada. Por favor, realize o
        cancelamento da NF e envie o comprovante de cancelamento ou entre em contato com o RH.
      </div>
    );
  }

  if (instrucao && !instrucao.nfStatus) {
    return (
      <>
      <div className="flex flex-wrap items-center gap-3 border-b border-amber-500/30 bg-amber-500/[.09] px-6 py-2.5 text-sm text-amber-800 dark:text-amber-300">
        <FilePlus2 className="h-4 w-4 shrink-0" />
        <span className="min-w-[240px] flex-1">
          Competência <strong>{formatCompetencia(instrucao.competencia)}</strong> • Emitir NF para{" "}
          <strong>{instrucao.tomadorNome}</strong> no valor de{" "}
          <strong>R$ {formatBRLCents(instrucao.valor)}</strong>.
        </span>
        <Link to="/colaborador/demonstrativo" className="text-[13px] font-medium underline underline-offset-4">
          Ver composição do valor
        </Link>
        {pathname !== "/colaborador/nova" && (
          <Link
            to="/colaborador/nova"
            className="flex h-[30px] items-center rounded-[7px] bg-amber-800 px-3 text-[13px] font-medium text-white hover:bg-amber-900"
          >
            Enviar NF
          </Link>
        )}
      </div>
      {avisoBonificacao}
      </>
    );
  }

  if (avisoBonificacao) return avisoBonificacao;

  return null;
};

/**
 * Categoria do breadcrumb "Categoria › Título". Nas telas de detalhe é a lista
 * de onde se veio; nas demais, a seção do menu.
 */
function categoriaDaRota(pathname: string, role: string | undefined, nomeBonificacao: string): string {
  const detalhe: [RegExp, string][] = [
    [/^\/rh\/notas\/[^/]+/, "Notas Fiscais"],
    [/^\/rh\/folha\/[^/]+/, "Folha de Pagamento"],
    [/^\/rh\/bonificacao\/[^/]+/, nomeBonificacao],
    [/^\/rh\/beneficios\/[^/]+/, "Benefícios"],
    [/^\/colaborador\/notas\/[^/]+/, "Minhas NFs"],
    [/^\/rh\/(colaboradores|tomadores|departamentos)/, "Cadastros"],
    [/^\/rh\/(auditoria|configuracoes)/, "Administração"],
  ];
  for (const [re, nome] of detalhe) if (re.test(pathname)) return nome;
  if (pathname.startsWith("/rh")) return role === "financeiro" ? "Financeiro" : "Gente e Gestão";
  if (pathname.startsWith("/perfil")) return "Conta";
  return "Colaborador";
}

export const AppLayout = ({
  children,
  title,
  categoria,
}: {
  children: ReactNode;
  title: string;
  /** Sobrescreve a categoria do breadcrumb (por padrão vem da rota). */
  categoria?: string;
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nomeBonificacao = useNomeBonificacao();
  const migalha = categoria ?? categoriaDaRota(pathname, user?.role, nomeBonificacao);

  const avatarSrc = user?.avatarUrl ?? null;
  const initials = user?.name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("") ?? "";

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b bg-card pl-4 pr-6">
            <div className="flex min-w-0 items-center gap-3">
              <SidebarTrigger className="h-8 w-8 text-muted-foreground" />
              <span className="h-5 w-px bg-border" />
              <nav aria-label="Você está em" className="flex min-w-0 items-center gap-2 text-sm">
                <span className="whitespace-nowrap text-muted-foreground">{migalha}</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                <h1 className="truncate text-[15px] font-semibold text-foreground">{title}</h1>
              </nav>
            </div>
            <button
              onClick={() => navigate("/perfil")}
              className="flex shrink-0 items-center gap-3 rounded-[10px] py-1 pl-2.5 pr-1.5 transition-colors hover:bg-muted"
            >
              <div className="hidden text-right leading-tight sm:block">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                  {user?.role === "rh" ? "Gente e Gestão" : user?.department}
                </div>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-bold overflow-hidden shrink-0">
                {avatarSrc ? (
                  <img src={avatarSrc} alt={user?.name} className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
              </div>
            </button>
          </header>
          <CompetenciaBanner />
          <main className="min-w-0 flex-1 px-4 pb-12 pt-6 sm:px-7">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
};
